import React, { useState, useEffect } from 'react';

import { getUsers, deleteUser, resendWelcomeEmail, disableUser, enableUser } from '../services/api/userApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faUserShield, faUserTag, faTrash, faEnvelope, faBan, faCheck } from '@fortawesome/free-solid-svg-icons';
import ConfirmDialog from './ConfirmDialog';


const UserList = ({ onEditUser, onCreateUser, onManagePermissions, onManageRoles }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, action: null, userId: null });


  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers();
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteUser(selectedUser.id);
      setUsers(users.filter(user => user.id !== selectedUser.id));
      setSuccess('User deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete user');
    } finally {
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  const confirmResendEmail = (user) => {
    setSelectedUser(user);
    setShowEmailModal(true);
  };

  const handleResendEmail = async () => {
    try {
      await resendWelcomeEmail(selectedUser.id);
      setSuccess('Welcome email sent successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to send welcome email');
    } finally {
      setShowEmailModal(false);
      setSelectedUser(null);
    }
  };
  const filterUsers = () => {
    if (statusFilter === 'all') {
      setFilteredUsers(users);
    } else if (statusFilter === 'active') {
      setFilteredUsers(users.filter(user => user.isEnabled));
    } else if (statusFilter === 'disabled') {
      setFilteredUsers(users.filter(user => !user.isEnabled));
    }
  };

  const handleDisable = (id) => {
    setConfirmDialog({
      isOpen: true,
      action: 'disable',
      userId: id,
      title: 'Disable User Account',
      message: 'Are you sure you want to disable this user account? The user will not be able to log in until the account is re-enabled.'
    });
  };

  const confirmDisable = async () => {
    try {
      await disableUser(confirmDialog.userId);
      setUsers(users.map(user => user.id === confirmDialog.userId ? { ...user, isEnabled: false } : user));
      setSuccess('User account disabled successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to disable user');
    } finally {
      setConfirmDialog({ isOpen: false, action: null, userId: null });
    }
  };

  const handleEnable = async (id) => {
    try {
      await enableUser(id);
      setUsers(users.map(user => user.id === id ? { ...user, isEnabled: true } : user));
      setSuccess('User account enabled successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to enable user');
    }
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action === 'disable') {
      confirmDisable();
    }
  };

  if (loading) return <div>Loading users...</div>;

  const getStatusBadge = (isEnabled) => {
    if (isEnabled) {
      return <span style={{ color: 'green', fontWeight: 'bold' }}>● Active</span>;
    }
    return <span style={{ color: 'gray', fontWeight: 'bold' }}>● Disabled</span>;
  };

  return (
    <div className="user-list">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog({ isOpen: false, action: null, userId: null })}
        confirmText="Disable"
        confirmButtonClass="btn-warning"
      />
      <div className="user-list-header">
        <h2>Users</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label htmlFor="statusFilter">Filter by Status:</label>
          <select 
            id="statusFilter"
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-select"
            style={{ width: 'auto' }}
          >
            <option value="all">All</option>
            <option value="active">Active Only</option>
            <option value="disabled">Disabled</option>
          </select>
          <button onClick={onCreateUser} className="btn btn-primary">
            Create User
          </button>
        </div>
      </div>
      
      {error && <div className="error">{error}</div>}
      {success && <div className="success" style={{color: 'green', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', padding: '10px', borderRadius: '4px', marginBottom: '15px'}}>{success}</div>}
      
      <table className="users-table">
        <thead>
          <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map(user => (
            <tr key={user.id}>
              <td>{user.firstName}</td>
              <td>{user.lastName}</td>
              <td>{user.email}</td>
              <td>{getStatusBadge(user.isEnabled)}</td>
              <td>{new Date(user.createdAt).toLocaleDateString()}</td>
              <td>
                <button 
                  onClick={() => onEditUser(user)} 
                  className="btn btn-outline-secondary btn-sm mx-1"
                  title="Edit User"
                >
                  <FontAwesomeIcon icon={faEdit} aria-hidden="true" /> <span className="sr-only">Edit</span>
                </button>
                <button 
                  onClick={() => onManagePermissions(user)} 
                  className="btn btn-outline-info btn-sm mx-1"
                  title="Manage Permissions"
                >
                  <FontAwesomeIcon icon={faUserShield} aria-hidden="true" /> <span className="sr-only">Permissions</span>
                </button>
                <button 
                  onClick={() => onManageRoles(user)} 
                  className="btn btn-outline-primary btn-sm mx-1"
                  title="Manage Roles"
                >
                  <FontAwesomeIcon icon={faUserTag} aria-hidden="true" /> <span className="sr-only">Roles</span>
                </button>
                {user.isEnabled ? (
                  <button 
                    onClick={() => handleDisable(user.id)} 
                    className="btn btn-outline-warning btn-sm mx-1"
                    title="Disable Account"
                  >
                    <FontAwesomeIcon icon={faBan} aria-hidden="true" /> <span className="sr-only">Disable</span>
                  </button>
                ) : (
                  <button 
                    onClick={() => handleEnable(user.id)} 
                    className="btn btn-outline-success btn-sm mx-1"
                    title="Enable Account"
                  >
                    <FontAwesomeIcon icon={faCheck} aria-hidden="true" /> <span className="sr-only">Enable</span>
                  </button>
                )}
                {!user.lastLoginAt && (
                  <button 
                    onClick={() => confirmResendEmail(user)} 
                    className="btn btn-outline-success btn-sm mx-1"
                    title="Resend Welcome Email"
                  >
                    <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" /> <span className="sr-only">Resend Email</span>
                  </button>
                )}
                <button 
                  onClick={() => confirmDelete(user)} 
                  className="btn btn-outline-danger btn-sm mx-1"
                  title="Delete User"
                >
                  <FontAwesomeIcon icon={faTrash} aria-hidden="true" /> <span className="sr-only">Delete</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete user <strong>{selectedUser?.email}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend Email Confirmation Modal */}
      {showEmailModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Resend Welcome Email</h5>
                <button type="button" className="btn-close" onClick={() => setShowEmailModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Resend welcome email to <strong>{selectedUser?.email}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEmailModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleResendEmail}>Send Email</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;